import React, { useEffect, useState } from 'react';
import { useUserStore } from '@core/user/useUserStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useSiteStore } from '@core/site/useSiteStore';
import { useClientStore } from '@core/client/useClientStore';
import { SiteStatus } from '@core/generated/gql-types';
import { generateFriendlyAddress } from '@core/util/google-places.service';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { Skeleton } from '@/components/shared/Skeleton';
import Button from '@/components/Button';
import ClientSiteDetails from '@/modules/clients/components/ClientSiteDetails';
import SiteContactCard from '@/modules/clients/components/SiteContactCard';
import useFeatureView from '@/hooks/useFeatureView';
import { getSitesListView } from '@/modules/sites/views/sitesList.view';
import { useSitesListData } from '@/modules/sites/hooks/useSitesListData';
import SiteModal from '@/modules/sites/components/SiteModal';

interface ClientSitesProps {
  /**
   * Client whose sites to show. Provided by the tenant portal (client detail
   * tab). When omitted (client portal), it falls back to the current user's
   * client.
   */
  clientId?: string;
}

const ClientSites: React.FC<ClientSitesProps> = ({ clientId }) => {
  const { getTerm } = useTemplateStore();
  const { currentUser } = useUserStore();
  const resolvedClientId = clientId ?? currentUser?.clientId ?? undefined;

  const view = useFeatureView(getSitesListView);
  const { sites, isLoading } = useSitesListData(view, resolvedClientId);
  const { fetchSitesForClient } = useSiteStore();
  const { fetchUsersByClient, fetchInvitesForClient } = useClientStore();
  const [hasFetched, setHasFetched] = useState(false);

  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  const selectedSite = sites.find((site) => site.id === selectedSiteId) ?? null;

  useEffect(() => {
    if (resolvedClientId && !hasFetched) {
      setHasFetched(true);
      fetchSitesForClient(resolvedClientId);
      fetchUsersByClient(resolvedClientId);
      fetchInvitesForClient(resolvedClientId);
    }
  }, [
    resolvedClientId,
    hasFetched,
    fetchSitesForClient,
    fetchUsersByClient,
    fetchInvitesForClient,
  ]);

  if (selectedSite && resolvedClientId) {
    return (
      <div className="grid h-full w-full grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <ClientSiteDetails
          site={selectedSite}
          clientId={resolvedClientId}
          onBack={() => setSelectedSiteId(null)}
        />
        <div className="flex flex-col gap-6">
          <SiteContactCard site={selectedSite} index={0} title="Contact 1" />
          <SiteContactCard site={selectedSite} index={1} title="Contact 2" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full rounded-lg bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Sites</h2>
        {view.actions.showAddSite && (
          <Button
            variant="primary"
            onClick={() => setIsSiteModalOpen(true)}
            className="gap-2 !px-4 !py-2 !text-sm"
          >
            + Add a Site
          </Button>
        )}
      </div>
      <div className="space-y-4">
        <p className="text-gray-600">
          Manage sites for this {getTerm(TemplateTerm.CLIENT, false)}.
        </p>

        <div className="flex-1 overflow-auto rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  Address
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && sites.length === 0 ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <tr key={index}>
                    <td className="border-t border-gray-200 px-4 py-3 align-middle">
                      <Skeleton height="h-4" width="w-32" />
                    </td>
                    <td className="border-t border-gray-200 px-4 py-3 align-middle">
                      <Skeleton height="h-4" width="w-48" />
                    </td>
                    <td className="border-t border-gray-200 px-4 py-3 align-middle">
                      <Skeleton height="h-6" width="w-16" />
                    </td>
                  </tr>
                ))
              ) : sites.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="border-t border-gray-200 px-4 py-10 text-center"
                  >
                    <div className="flex flex-col items-center gap-4">
                      <p className="text-gray-500">No Sites Found</p>
                      <Button
                        variant="primary"
                        onClick={() => setIsSiteModalOpen(true)}
                        className="gap-2 !px-4 !py-2 !text-sm"
                      >
                        <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                        Add a Site
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                sites.map((site) => (
                  <tr
                    key={site.id}
                    onClick={() => setSelectedSiteId(site.id)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="border-t border-gray-200 px-4 py-3 align-middle">
                      {site.name}
                    </td>
                    <td className="border-t border-gray-200 px-4 py-3 align-middle text-gray-700">
                      {generateFriendlyAddress(site.address ?? {}) || '-'}
                    </td>
                    <td className="border-t border-gray-200 px-4 py-3 align-middle">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          site.status === SiteStatus.Active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-gray-600'
                        }`}
                      >
                        {site.status === SiteStatus.Active
                          ? 'ENABLED'
                          : 'DISABLED'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <SiteModal
        isOpen={isSiteModalOpen}
        clientId={resolvedClientId ?? ''}
        onClose={() => setIsSiteModalOpen(false)}
      />
    </div>
  );
};

export default ClientSites;
